import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorCode, errorMessage, isErrorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { FollowUpRequestForm } from '@/follow-up/FollowUpRequestForm';
import { useFollowUpsAlert } from '@/follow-up/FollowUpsAlertContext';
import {
  StudentSelection,
  studentOptionLabel,
} from '@/follow-up/StudentSelection';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
  type Person,
} from '~/hooks/useAsyncPeoplePicker';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';

type GroupFollowUpRequestCreate =
  components['schemas']['GroupFollowUpRequestCreate'];

type UserIDSearchParams = { user_id?: number };

/**
 * The message legacy names for each of create's own refusals, keyed by the
 * `error_code` the server raises. The server decides which fires and in what
 * order; the client only draws it. `follow_up_request_already_open` is absent
 * deliberately — it has its own AlertBox above the subnav, where legacy renders
 * it.
 */
const CREATE_ERROR_KEYS = {
  follow_up_objective_required: 'wbs.you-must-select-a-new-follow-up-objective',
  follow_up_country_required: 'wbs.please-select-a-country',
  follow_up_address_required: 'wbs.please-enter-an-address',
  follow_up_consent_required:
    'wbs.please-make-sure-you-have-confirmed-to-all-the-permissions',
} as const;

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/follow-up-requests/new',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): UserIDSearchParams => {
    const id = Number(search.user_id);
    return { user_id: Number.isInteger(id) && id > 0 ? id : undefined };
  },
});

/**
 * The two-step *Create a New Request* wizard —
 * `groups/single/study-group/follow-ups/follow-up-requests/new.php`, which is
 * two GETs branching on `?user_id=` (`:9`) exactly as this route does.
 *
 * Legacy's duplicate guard is on step 1 ONLY and is trivially bypassed: opening
 * `new/?user_id=1118` for a student with an open request renders a full,
 * submittable form, and `save_follow_up_request`
 * (`class-follow-up-request.php:182-186`) re-checks nothing but the nonce. That
 * is ported as written — the notice fires on Next, the form still renders on a
 * direct link, and the create answers 409.
 */
function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { groupName } = Route.useParams();
  const { setDuplicateBlocked, setCreatedRequestId } = useFollowUpsAlert();

  useEffect(() => () => setDuplicateBlocked(false), [setDuplicateBlocked]);

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  // What the person is typing, and what the query is keyed on. Legacy's select2
  // debounces at 250ms; without it every keystroke past the third is a request.
  const [term, setTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () => setSearchTerm(term),
      PEOPLE_PICKER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [term]);

  const { data: matches, isFetching: matchesFetching } =
    api.listGroupFollowUpRequestStudentOptions.useQuery(
      {
        path: { group_id: groupId ?? 0 },
        query: { search: searchTerm, limit: PEOPLE_PICKER_PAGE_SIZE },
      },
      {
        enabled:
          groupId !== undefined && searchTerm.length >= PEOPLE_PICKER_MIN_QUERY,
      },
    );

  const results = useMemo<Person[]>(
    () =>
      (matches?.data ?? []).map((option) => ({
        id: option.id,
        name: studentOptionLabel(option.display_name, option.id),
      })),
    [matches],
  );

  // The student picked on step 1, held only until the draft answers whether they
  // already hold a live request. Once the navigation has landed the id comes
  // from the URL instead, so the same draft query serves both and the second
  // read is a cache hit — which is why a candidate equal to `?user_id=` is no
  // longer pending. A candidate that DIFFERS wins over the URL: the recovery
  // picker below is only on screen because `?user_id=` names a student whose
  // draft 404s, and keying the query on that dead id would fail every
  // replacement pick too.
  const [candidateId, setCandidateId] = useState<number | null>(null);
  const pendingCandidateId =
    candidateId !== null && candidateId !== search.user_id ? candidateId : null;
  const targetStudentId = pendingCandidateId ?? search.user_id ?? null;

  const {
    data: draft,
    isError: draftFailed,
    error: draftError,
  } = api.getGroupFollowUpRequestDraft.useQuery(
    {
      path: { group_id: groupId ?? 0 },
      query: { student_id: targetStudentId ?? 0 },
    },
    { enabled: groupId !== undefined && targetStudentId !== null },
  );

  useEffect(() => {
    if (candidateId === null) return;
    // Held until the navigation has actually landed, not until it is requested:
    // clearing it beside `navigate()` drops `targetStudentId` for the render
    // between the two and flashes step 1 back onto the screen.
    if (search.user_id === candidateId) {
      setCandidateId(null);
      return;
    }
    if (draftFailed) {
      setCandidateId(null);
      return;
    }
    if (draft === undefined) return;
    // Where LEGACY renders it: on the step-1 submit, above the subnav
    // (`class-follow-up-request.php:156-177`). The CREATE still 409s.
    if (draft.has_live_request) {
      setCandidateId(null);
      setDuplicateBlocked(true);
      return;
    }
    void navigate({ to: './', search: { user_id: candidateId } });
  }, [
    candidateId,
    draft,
    draftFailed,
    navigate,
    search.user_id,
    setDuplicateBlocked,
  ]);

  const {
    mutateAsync: createRequest,
    isPending: creating,
    error: createError,
  } = api.createGroupFollowUpRequest.useMutation();

  const handleClose = () => {
    if (search.user_id !== undefined) {
      void navigate({ to: './', search: {}, replace: true });
      return;
    }
    void navigate({
      to: '/groups/$groupName/follow-ups/follow-up-requests/',
      params: { groupName },
    });
  };

  const handleNext = (studentId: number) => {
    setDuplicateBlocked(false);
    setCandidateId(studentId);
  };

  const handleSubmit = async (
    body: GroupFollowUpRequestCreate,
  ): Promise<void> => {
    if (groupId === undefined) return;
    let created: components['schemas']['GroupFollowUpRequestCreated'];
    try {
      created = await createRequest({ path: { group_id: groupId }, body });
    } catch (error) {
      // The 409 is the duplicate notice, rendered where the step-1 one is; every
      // other refusal is drawn from `createError` beside the form.
      if (isErrorCode(error, 'follow_up_request_already_open')) {
        setDuplicateBlocked(true);
      }
      return;
    }
    setCreatedRequestId(String(created.id));
    // Both group lists can now be stale: the row is `pending` (subnav 2) unless
    // the student is unverified-postal, in which case it was auto-approved and
    // lands on subnav 1 instead.
    await Promise.all([
      api.listGroupPendingFollowUpRequests.invalidateQueries(),
      api.listGroupFollowUpRequests.invalidateQueries(),
    ]);
    // Legacy's `redirect_to` hidden field (`new.php:49-50`).
    void navigate({
      to: '/groups/$groupName/follow-ups/pending-requests/',
      params: { groupName },
    });
  };

  const createFailureCode = errorCode(createError);
  const createFailureText =
    createError == null ||
    createFailureCode === 'follow_up_request_already_open'
      ? null
      : createFailureCode !== undefined &&
          createFailureCode in CREATE_ERROR_KEYS
        ? t(
            CREATE_ERROR_KEYS[
              createFailureCode as keyof typeof CREATE_ERROR_KEYS
            ],
          )
        : // Translation Needed — EN-only in the catalogue too, the same fallback
          // `FollowUpVerifyModal` uses for a coded refusal with no message.
          errorMessage(
            createError,
            t('wbs.there-was-a-problem-with-your-submission'),
          );

  const picker = (
    <StudentSelection
      source={{
        results,
        // The control stays loading through the debounce window too, so it does
        // not flash "no results" between a keystroke and the request it caused.
        loading:
          term.length >= PEOPLE_PICKER_MIN_QUERY &&
          (term !== searchTerm || matchesFetching),
        onSearch: setTerm,
      }}
      onNext={handleNext}
      submitting={pendingCandidateId !== null}
    />
  );

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.new-follow-up-request')}
          helpKey="New Follow-up Request"
        />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleClose} />
        {createFailureText !== null && (
          <AlertBox variant="error">{createFailureText}</AlertBox>
        )}
        {search.user_id === undefined || pendingCandidateId !== null ? (
          // Step 1 — including a replacement pick made from the recovery picker,
          // whose draft is keyed on the new student and not on the dead param.
          picker
        ) : draftFailed ? (
          // The draft 404s for a student outside the group's derived roster,
          // which a hand-edited `?user_id=` can reach and the picker cannot.
          // Legacy has no such state at all — `new.php:31` takes `$_GET` verbatim
          // — so the recovery is the only other screen this route has.
          <>
            <AlertBox variant="error">
              {errorMessage(
                draftError,
                t('wbs.there-was-a-problem-with-your-submission'),
              )}
            </AlertBox>
            {picker}
          </>
        ) : draft === undefined ? null : (
          <FollowUpRequestForm
            // Seven fields seed from `draft` in `useState`; the key forces a
            // remount so a student swap can never submit the previous
            // student's contact snapshot.
            key={draft.student.id}
            draft={draft}
            submitting={creating}
            onSubmit={(body) => void handleSubmit(body)}
          />
        )}
      </FormContainer>
    </>
  );
}
